import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { Drawer, Button, Tabs, Alert, message, Checkbox, Modal } from 'antd';
import { MenuOutlined, RetweetOutlined, LeftOutlined, RightOutlined, AppstoreOutlined } from '@ant-design/icons';
import './QuestionAnswering.css';

function QuestionAnswering({ bank, onBack }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]); // 多选题选择的选项
  const [explanation, setExplanation] = useState(null);
  const [detailedExplanation, setDetailedExplanation] = useState(null);
  const [relatedQuestions, setRelatedQuestions] = useState([]);
  const [score, setScore] = useState(0);
  const [wrongAnswers, setWrongAnswers] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isDoingWrongQuestion, setIsDoingWrongQuestion] = useState(false);
  const [currentWrongQuestion, setCurrentWrongQuestion] = useState(null);
  const [wrongQuestionsQueue, setWrongQuestionsQueue] = useState([]);
  const [progress, setProgress] = useState({
    total: bank.count || 0,
    answered: 0,
    correct: 0,
    wrong: 0
  });

  // 添加题目答题记录状态
  const [questionRecords, setQuestionRecords] = useState({});
  // 添加最近做题位置记录
  const [lastPosition, setLastPosition] = useState({
    bankId: bank.id,
    index: 0,
    isWrongQuestion: false,
    wrongQuestionId: null
  });

  // 添加题目导航相关状态
  const [navigationVisible, setNavigationVisible] = useState(false);

  // 加载题目数据
  const [questionBank, setQuestionBank] = useState(null);
  useEffect(() => {
    const loadQuestions = async () => {
      try {
        // 检查是否需要强制刷新题库数据
        const shouldForceRefresh = () => {
          const lastRefresh = localStorage.getItem(`bankRefresh-${bank.id}`);
          if (!lastRefresh) return true;
          
          const lastRefreshTime = new Date(lastRefresh);
          const now = new Date();
          const timeDiff = now - lastRefreshTime;
          
          // 如果超过2分钟，强制刷新
          return timeDiff > 2 * 60 * 1000;
        };

        const forceRefresh = shouldForceRefresh();
        
        // 移除path开头的斜杠，获取相对路径
        const bankPath = bank.path.startsWith('/') ? bank.path.substring(1) : bank.path;
        const url = forceRefresh 
          ? `/question-banks/${bankPath}?_t=${Date.now()}`
          : `/question-banks/${bankPath}`;
        
        const response = await fetch(url, {
          cache: forceRefresh ? 'no-cache' : 'default',
          headers: forceRefresh ? {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          } : {}
        });
        
        const data = await response.json();
        setQuestionBank(data);
        
        if (forceRefresh) {
          localStorage.setItem(`bankRefresh-${bank.id}`, new Date().toISOString());
          console.log(`🔄 已刷新题库: ${bank.title}`);
        }
        
        // 更新进度中的总题目数
        setProgress(prev => ({
          ...prev,
          total: data.totalCount || data.questions.length
        }));

        // 加载本地存储的答题记录
        const savedRecords = localStorage.getItem(`questionRecords-${bank.id}`);
        if (savedRecords) {
          setQuestionRecords(JSON.parse(savedRecords));
        }

        // 检查是否有全科错题练习的标记
        const allWrongPracticeKeys = Object.keys(localStorage).filter(key => 
          key.startsWith('startAllWrongQuestionPractice-')
        );
        
        if (allWrongPracticeKeys.length > 0) {
          const practiceKey = allWrongPracticeKeys[0];
          const practiceData = JSON.parse(localStorage.getItem(practiceKey));
          
          if (practiceData && practiceData.allWrongQuestions) {
            setWrongQuestionsQueue(practiceData.allWrongQuestions);
            setIsDoingWrongQuestion(true);
            setCurrentWrongQuestion(practiceData.allWrongQuestions[0]);
            
            // 清除标记
            localStorage.removeItem(practiceKey);
            
            message.success(`开始全科错题练习，共 ${practiceData.allWrongQuestions.length} 题（按错误次数排序）`);
            return; // 跳过正常的位置恢复逻辑
          }
        }

        // 加载最近一次做题的位置
        const savedPosition = localStorage.getItem(`lastPosition-${bank.id}`);
        if (savedPosition) {
          const position = JSON.parse(savedPosition);
          
          // 恢复题库中的位置
          if (!position.isWrongQuestion) {
            setCurrentIndex(position.index);
            message.info(`已恢复到上次做题位置: 第 ${position.index + 1} 题`);
          } 
          // 恢复错题集中的位置
          else if (position.wrongQuestionId) {
            const savedWrongAnswers = localStorage.getItem(`wrongAnswers-${bank.id}`);
            if (savedWrongAnswers) {
              const wrongAnswersList = JSON.parse(savedWrongAnswers);
              const targetQuestion = wrongAnswersList.find(q => q.question === position.wrongQuestionId);
              
              if (targetQuestion) {
                setIsDoingWrongQuestion(true);
                setCurrentWrongQuestion(targetQuestion);
                message.info('已恢复到上次错题练习位置');
              }
            }
          }
          
          setLastPosition(position);
        }
      } catch (error) {
        console.error('加载题目失败:', error);
      }
    };
    loadQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bank.path, bank.count, bank.id]);

  // 保存当前位置信息到localStorage
  const saveLastPosition = useCallback((index, isWrong = false, wrongQuestionId = null) => {
    const position = {
      bankId: bank.id,
      index,
      isWrongQuestion: isWrong,
      wrongQuestionId,
      timestamp: new Date().getTime()
    };
    
    setLastPosition(position);
    localStorage.setItem(`lastPosition-${bank.id}`, JSON.stringify(position));
  }, [bank.id]);

  // 题目索引变化时保存位置
  useEffect(() => {
    if (!questionBank || isDoingWrongQuestion) return;
    saveLastPosition(currentIndex);
  }, [currentIndex, questionBank, bank.id, isDoingWrongQuestion, saveLastPosition]);

  // 错题练习时保存位置
  useEffect(() => {
    if (!isDoingWrongQuestion || !currentWrongQuestion) return;
    saveLastPosition(currentIndex, true, currentWrongQuestion.question);
  }, [isDoingWrongQuestion, currentWrongQuestion, bank.id, currentIndex, saveLastPosition]);

  useEffect(() => {
    const savedProgress = localStorage.getItem(`progress-${bank.id}`);
    if (savedProgress) {
      setProgress(JSON.parse(savedProgress));
    }
    const savedWrongAnswers = localStorage.getItem(`wrongAnswers-${bank.id}`);
    if (savedWrongAnswers) {
      setWrongAnswers(JSON.parse(savedWrongAnswers));
    }
  }, [bank.id]);

  // 添加滑动相关的状态
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const touchStartY = useRef(0);
  const touchEndY = useRef(0);
  const questionCardRef = useRef(null);
  const [swipeDirection, setSwipeDirection] = useState(null); // 用于显示滑动方向的视觉反馈
  const [showSwipeHint, setShowSwipeHint] = useState(false);

  // 监听当前题目变化，加载当前题目的答题记录
  useEffect(() => {
    if (!questionBank || isDoingWrongQuestion) return;
    
    const currentQuestionItem = questionBank.questions[currentIndex];
    if (!currentQuestionItem) return; // 防止currentQuestionItem为undefined
    
    const currentQuestionId = currentQuestionItem.question;
    if (currentQuestionId && questionRecords[currentQuestionId]) {
      const record = questionRecords[currentQuestionId];
      setUserAnswer(record.userAnswer);
      setExplanation(record.explanation);
      setDetailedExplanation(record.detailedExplanation);
      setRelatedQuestions(record.relatedQuestions || []);
    } else {
      // 如果没有答题记录，重置状态
      setUserAnswer(null);
      setExplanation(null);
      setDetailedExplanation(null);
      setRelatedQuestions([]);
    }
  }, [currentIndex, questionBank, isDoingWrongQuestion, questionRecords]);

  // 清理和改善解析内容
  const cleanExplanation = (explanation, questionText, correctAnswer) => {
    if (!explanation) return '暂无解析';
    
    // 检测无用的模板化解析
    const uselessPatterns = [
      /因为这符合.*教材中关于.*的基本知识点/g,
      /是正确的，因为这符合.*教材中关于.*的基本知识点/g,
      /本题考察.*的内容，涉及.*知识点。[\s\S]*?是正确的，因为这符合.*教材中关于.*的基本知识点。/g,
      /默认答案，请查阅正确答案/g,
      /A: 默认答案，请查阅正确答案/g
    ];
    
    let cleanedExplanation = explanation;
    
    // 移除无用的模板内容
    uselessPatterns.forEach(pattern => {
      cleanedExplanation = cleanedExplanation.replace(pattern, '');
    });
    
    // 清理多余的空行和空白
    cleanedExplanation = cleanedExplanation
      .replace(/\n\n\n+/g, '\n\n')
      .replace(/^\s+|\s+$/g, '')
      .trim();
    
    // 如果清理后内容太少或者仍然是无用内容，提供默认提示
    if (cleanedExplanation.length < 20 || 
        cleanedExplanation.includes('默认答案') ||
        cleanedExplanation.includes('请查阅正确答案') ||
        /^[\s\n]*$/.test(cleanedExplanation)) {
      
      // 提取题目中的关键词
      const topicMatch = questionText.match(/\d+\.\s*\([^)]+\)\s*(.+?)[（(]/);
      const topic = topicMatch ? topicMatch[1].trim() : '相关概念';
      
      return `该题考查${topic}的基本概念。\n\n正确答案：${correctAnswer}\n\n建议：请参考教材中相关章节的内容，理解该知识点的核心要点和应用。`;
    }
    
    return cleanedExplanation;
  };

  // 判断题目类型（单选或多选）
  const getQuestionType = (question) => {
    if (!question || !question.question) return 'single';
    
    // 从题目文本中判断
    if (question.question.includes('(多选题)')) {
      return 'multiple';
    }
    
    // 检查是否明确标记为单选题
    if (question.question.includes('(单选题)')) {
      return 'single';
    }
    
    // 从答案格式判断（多选题答案应该包含多个字母，如 "ABC" 或 "AB"）
    if (question.answer) {
      // 提取答案中的字母部分（冒号前的部分）
      const answerLetters = question.answer.split(':')[0] || question.answer;
      // 如果答案字母部分长度大于1且都是有效字母，则为多选题
      if (answerLetters.length > 1 && /^[A-E]+$/.test(answerLetters)) {
        return 'multiple';
      }
    }
    
    // 默认为单选题
    return 'single';
  };

  // 判断当前题目是否为多选题
  const isMultipleChoice = () => {
    const currentQuestion = isDoingWrongQuestion 
      ? currentWrongQuestion 
      : questionBank?.questions[currentIndex];
    return getQuestionType(currentQuestion) === 'multiple';
  };

  // 处理单选题选择
  const handleSingleAnswerSelect = async (option) => {
    if (userAnswer || !questionBank) return;
    
    setUserAnswer(option);
    
    // 确定当前处理的题目（正常题库或错题）
    const currentQuestion = isDoingWrongQuestion 
      ? currentWrongQuestion 
      : questionBank.questions[currentIndex];
    
    if (!currentQuestion) {
      console.error("当前题目不存在");
      return;
    }
    
    // 从选项中提取选项字母
    const selectedLetter = option.match(/^([A-E])/)?.[1];
    
    // 从正确答案中提取答案字母
    const correctLetter = currentQuestion.answer?.match(/^([A-E])/)?.[1];
    
    const isCorrect = selectedLetter === correctLetter;
    
    await processAnswer(currentQuestion, option, isCorrect);
  };

  // 处理多选题选择
  const handleMultipleAnswerSelect = (option) => {
    if (userAnswer) return; // 已提交答案则不允许再选择
    
    const optionLetter = option.match(/^([A-E])/)?.[1];
    
    setSelectedOptions(prev => {
      if (prev.includes(optionLetter)) {
        // 如果已选中，则取消选择
        return prev.filter(letter => letter !== optionLetter);
      } else {
        // 如果未选中，则添加到选择列表
        return [...prev, optionLetter].sort();
      }
    });
  };

  // 提交多选题答案
  const submitMultipleAnswer = async () => {
    if (!questionBank || selectedOptions.length === 0) {
      message.warning('请至少选择一个选项');
      return;
    }
    
    const submittedAnswer = selectedOptions.sort().join('');
    setUserAnswer(submittedAnswer);
    
    const currentQuestion = isDoingWrongQuestion 
      ? currentWrongQuestion 
      : questionBank.questions[currentIndex];
    
    if (!currentQuestion) {
      console.error("当前题目不存在");
      return;
    }
    
      // 修复多选题答案比较逻辑
    const correctAnswer = currentQuestion.answer || '';
    
    // 提取正确答案中的字母（处理可能包含选项内容的格式）
    const correctLetters = correctAnswer.match(/[A-E]/g) || [];
    const correctLettersSet = new Set(correctLetters);
    const userLettersSet = new Set(selectedOptions);
    
    // 多选题判断逻辑：用户选择的选项必须与正确答案完全一致
    const isCorrect = correctLettersSet.size === userLettersSet.size && 
                     [...correctLettersSet].every(letter => userLettersSet.has(letter));
    
    await processAnswer(currentQuestion, submittedAnswer, isCorrect);
  };

  // 统一的答案处理逻辑
  const processAnswer = async (currentQuestion, answer, isCorrect) => {
    // 更新统计（仅针对非错题练习模式）
    if (!isDoingWrongQuestion) {
      const newProgress = {
        ...progress,
        answered: progress.answered + 1,
        correct: isCorrect ? progress.correct + 1 : progress.correct,
        wrong: isCorrect ? progress.wrong : progress.wrong + 1
      };
      setProgress(newProgress);
      localStorage.setItem(`progress-${bank.id}`, JSON.stringify(newProgress));
      setScore(isCorrect ? score + 1 : score);
    }

    // 设置解析内容
    setExplanation(cleanExplanation(currentQuestion.explanation, currentQuestion.question, currentQuestion.answer));
    setDetailedExplanation(currentQuestion.detailedExplanation || '');
    setRelatedQuestions(currentQuestion.relatedQuestions || []);
    
    // 保存答题记录（仅针对非错题练习模式）
    if (!isDoingWrongQuestion) {
      const newQuestionRecords = {
        ...questionRecords,
        [currentQuestion.question]: {
          userAnswer: answer,
          isCorrect,
          explanation: cleanExplanation(currentQuestion.explanation, currentQuestion.question, currentQuestion.answer),
          detailedExplanation: currentQuestion.detailedExplanation || '',
          relatedQuestions: currentQuestion.relatedQuestions || []
        }
      };
      setQuestionRecords(newQuestionRecords);
      localStorage.setItem(`questionRecords-${bank.id}`, JSON.stringify(newQuestionRecords));
    }
    
    // 记录错题（仅针对非错题练习模式）
    if (!isDoingWrongQuestion && !isCorrect) {
      const existingWrongAnswerIndex = wrongAnswers.findIndex(q => 
        q.question === currentQuestion.question
      );
      
      if (existingWrongAnswerIndex === -1) {
        // 新错题，添加到错题集
        const newWrongAnswers = [...wrongAnswers, {
          ...currentQuestion,
          userAnswer: answer,
          wrongCount: 1,
          firstWrongAt: new Date().toISOString(),
          lastWrongAt: new Date().toISOString()
        }];
        setWrongAnswers(newWrongAnswers);
        localStorage.setItem(`wrongAnswers-${bank.id}`, JSON.stringify(newWrongAnswers));
      } else {
        // 已存在的错题，增加错误次数
        const updatedWrongAnswers = [...wrongAnswers];
        updatedWrongAnswers[existingWrongAnswerIndex] = {
          ...updatedWrongAnswers[existingWrongAnswerIndex],
          userAnswer: answer,
          wrongCount: (updatedWrongAnswers[existingWrongAnswerIndex].wrongCount || 1) + 1,
          lastWrongAt: new Date().toISOString(),
          status: undefined // 重置掌握状态
        };
        setWrongAnswers(updatedWrongAnswers);
        localStorage.setItem(`wrongAnswers-${bank.id}`, JSON.stringify(updatedWrongAnswers));
      }
    }
    
    // 处理错题模式下的答题结果
    if (isDoingWrongQuestion) {
      if (isCorrect) {
        const updatedWrongAnswers = wrongAnswers.map(q => 
          q.question === currentQuestion.question 
            ? { ...q, masteredAt: new Date().toISOString(), status: 'mastered' }
            : q
        );
        setWrongAnswers(updatedWrongAnswers);
        localStorage.setItem(`wrongAnswers-${bank.id}`, JSON.stringify(updatedWrongAnswers));
        message.success('答对了！该题已标记为已掌握');
      } else {
        // 在错题模式下答错，更新错误次数
        const updatedWrongAnswers = wrongAnswers.map(q => 
          q.question === currentQuestion.question 
            ? { 
                ...q, 
                userAnswer: answer,
                wrongCount: (q.wrongCount || 1) + 1,
                lastWrongAt: new Date().toISOString(),
                status: undefined // 重置掌握状态
              }
            : q
        );
        setWrongAnswers(updatedWrongAnswers);
        localStorage.setItem(`wrongAnswers-${bank.id}`, JSON.stringify(updatedWrongAnswers));
        message.error('回答错误，请查看解析');
      }
    } else if (isCorrect) {
      message.success('回答正确！');
    } else {
      message.error('回答错误，请查看解析');
    }
  };

  // 重置答题状态
  const resetAnswerState = () => {
    setUserAnswer(null);
    setSelectedOptions([]);
    setExplanation(null);
    setDetailedExplanation(null);
    setRelatedQuestions([]);
  };

  // 修改原有的handleAnswerSelect以支持题目类型判断
  const handleAnswerSelect = async (option) => {
    if (isMultipleChoice()) {
      handleMultipleAnswerSelect(option);
    } else {
      await handleSingleAnswerSelect(option);
    }
  };

  // 处理下一题
  const handleNextQuestion = useCallback(() => {
    if (isDoingWrongQuestion) {
      // 错题模式下的处理
      if (wrongQuestionsQueue.length > 0) {
        // 获取下一个错题
        const nextWrongQuestion = wrongQuestionsQueue[0];
        const remainingQuestions = wrongQuestionsQueue.slice(1);
        setWrongQuestionsQueue(remainingQuestions);
        setCurrentWrongQuestion(nextWrongQuestion);
        
        // 重置答题状态
        resetAnswerState();
        
        // 保存当前位置
        saveLastPosition(0, true, nextWrongQuestion.question);
        message.info(`进入下一道错题，剩余${remainingQuestions.length}题`);
      } else {
        // 错题练习完成，询问是否重新开始
        const shouldRestart = window.confirm('恭喜！您已完成所有错题练习。\n\n是否要重新开始错题练习？\n点击"确定"重新开始，点击"取消"退出错题练习模式。');
        
        if (shouldRestart) {
          // 重新开始错题练习
          practiceAllWrongQuestions();
        } else {
          // 退出错题练习模式
          message.success('已退出错题练习模式');
          setIsDoingWrongQuestion(false);
          setCurrentWrongQuestion(null);
          
          // 恢复到之前的位置
          if (lastPosition && lastPosition.index !== undefined) {
            setCurrentIndex(lastPosition.index);
          }
        }
      }
    } else {
      // 正常模式下的处理
      if (questionBank && currentIndex < questionBank.questions.length - 1) {
        // 更新到下一题
        const nextIndex = currentIndex + 1;
        setCurrentIndex(nextIndex);
        
        // 重置答题状态
        resetAnswerState();
        
        saveLastPosition(nextIndex);
      } else {
        message.info('已经是最后一题了');
      }
    }
  }, [isDoingWrongQuestion, wrongQuestionsQueue, questionBank, currentIndex, lastPosition, saveLastPosition]);

  // 处理上一题
  const handlePrevQuestion = useCallback(() => {
    if (isDoingWrongQuestion) {
      // 错题模式下的处理
      const currentWrongIndex = wrongQuestionsQueue.findIndex(q => 
        q.questionIndex === currentWrongQuestion?.questionIndex && 
        q.bankId === currentWrongQuestion?.bankId
      );
      
      if (currentWrongIndex > 0) {
        // 有上一道错题，直接跳转
        const prevWrongQuestion = wrongQuestionsQueue[currentWrongIndex - 1];
        setCurrentWrongQuestion(prevWrongQuestion);
        setCurrentIndex(prevWrongQuestion.questionIndex);
        resetAnswerState();
      } else {
        // 已经是第一道错题，询问是否退出
        if (window.confirm('已经是第一道错题了，是否退出错题练习模式？')) {
          setIsDoingWrongQuestion(false);
          setCurrentWrongQuestion(null);
          setWrongQuestionsQueue([]);
          
          // 恢复到之前的位置
          if (lastPosition && lastPosition.index !== undefined && !lastPosition.isWrongQuestion) {
            setCurrentIndex(lastPosition.index);
          } else {
            setCurrentIndex(0);
          }
          
          // 重置答题状态
          resetAnswerState();
          
          message.info('已退出错题练习模式');
        }
      }
    } else {
      // 正常模式下的处理
      if (currentIndex > 0) {
        // 更新到上一题
        const prevIndex = currentIndex - 1;
        setCurrentIndex(prevIndex);
        
        // 重置答题状态
        resetAnswerState();
        
        saveLastPosition(prevIndex);
      } else {
        message.info('已经是第一题了');
      }
    }
  }, [isDoingWrongQuestion, wrongQuestionsQueue, currentWrongQuestion, lastPosition, currentIndex, saveLastPosition]);

  // 清除所有答题记录
  const clearAllRecords = () => {
    if (window.confirm('确定要清除所有答题记录吗？这将重置所有题目状态和做题位置。')) {
      setQuestionRecords({});
      localStorage.removeItem(`questionRecords-${bank.id}`);
      localStorage.removeItem(`lastPosition-${bank.id}`);
      resetAnswerState();
      setCurrentIndex(0);
      message.success('已清除所有答题记录和做题位置');
    }
  };

  const showDrawer = () => {
    setDrawerVisible(true);
  };

  const onCloseDrawer = () => {
    setDrawerVisible(false);
  };

  // 新增显示答案功能
  const showAnswer = () => {
    if (!questionBank) return;
    
    const currentQuestion = isDoingWrongQuestion 
      ? currentWrongQuestion 
      : questionBank.questions[currentIndex];
      
    if (!currentQuestion) {
      message.error('当前题目不存在');
      return;
    }
      
    setUserAnswer(currentQuestion.answer || ''); // 直接设置为正确答案
    
    // 对于多选题，也要设置选中状态以便UI显示
    if (isMultipleChoice() && currentQuestion.answer) {
      const correctAnswers = currentQuestion.answer.split('').filter(letter => /[A-E]/.test(letter));
      setSelectedOptions(correctAnswers);
    }
    
    // 设置解析内容
    setExplanation(cleanExplanation(currentQuestion.explanation, currentQuestion.question, currentQuestion.answer));
    setDetailedExplanation(currentQuestion.detailedExplanation || '');
    setRelatedQuestions(currentQuestion.relatedQuestions || []);
  };
  
  // 练习单个错题
  const practiceWrongQuestion = (wrongQuestion) => {
    setIsDoingWrongQuestion(true);
    setCurrentWrongQuestion(wrongQuestion);
    setWrongQuestionsQueue([]);
    resetAnswerState();
    setDrawerVisible(false);
    // 保存当前错题练习位置
    saveLastPosition(0, true, wrongQuestion.question);
    message.info('进入错题练习模式');
  };
  
  // 连续练习所有错题
  const practiceAllWrongQuestions = () => {
    // 过滤出未掌握的错题
    const unmasteredQuestions = wrongAnswers.filter(q => q.status !== 'mastered');
    
    // 如果没有未掌握的错题，则练习所有错题
    const questionsToUse = unmasteredQuestions.length > 0 ? unmasteredQuestions : wrongAnswers;
    
    if (questionsToUse.length === 0) {
      message.info('没有错题可以练习');
      return;
    }
    
    // 随机打乱错题顺序，提高练习效果
    const shuffledWrongAnswers = [...questionsToUse].sort(() => Math.random() - 0.5);
    
    // 设置第一道题和剩余队列
    const firstQuestion = shuffledWrongAnswers[0];
    const remainingQuestions = shuffledWrongAnswers.slice(1);
    
    setIsDoingWrongQuestion(true);
    setCurrentWrongQuestion(firstQuestion);
    setWrongQuestionsQueue(remainingQuestions);
    resetAnswerState();
    setDrawerVisible(false);
    
    // 保存当前错题练习位置
    saveLastPosition(0, true, firstQuestion.question);
    
    if (unmasteredQuestions.length > 0) {
      message.info(`开始练习${unmasteredQuestions.length}道未掌握的错题，可重复练习`);
    } else {
      message.info(`开始重做所有${wrongAnswers.length}道错题，可重复练习`);
    }
  };
  
  // 清空错题集
  const clearWrongAnswers = () => {
    if (window.confirm('确定要清空错题集吗？这将删除所有错题记录（包括已掌握的题目）。')) {
      setWrongAnswers([]);
      localStorage.setItem(`wrongAnswers-${bank.id}`, JSON.stringify([]));
      message.success('错题集已清空');
    }
  };

  // 新增：清除已掌握的错题
  const clearMasteredQuestions = () => {
    const unmasteredQuestions = wrongAnswers.filter(q => q.status !== 'mastered');
    if (window.confirm(`确定要清除${wrongAnswers.length - unmasteredQuestions.length}道已掌握的错题吗？`)) {
      setWrongAnswers(unmasteredQuestions);
      localStorage.setItem(`wrongAnswers-${bank.id}`, JSON.stringify(unmasteredQuestions));
      message.success('已掌握的错题已清除');
    }
  };

  // 修改useEffect添加滑动提示
  useEffect(() => {
    // 检查是否是第一次使用（显示滑动提示）
    const hasSeenSwipeHint = localStorage.getItem('hasSeenSwipeHint');
    if (!hasSeenSwipeHint) {
      setShowSwipeHint(true);
      setTimeout(() => {
        setShowSwipeHint(false);
        localStorage.setItem('hasSeenSwipeHint', 'true');
      }, 5000); // 5秒后自动隐藏提示
    }
  }, []);

  // 完善触摸事件处理器
  useEffect(() => {
    const handleTouchStart = (e) => {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
      setSwipeDirection(null);
    };
    
    const handleTouchMove = (e) => {
      if (!touchStartX.current) return;
      
      const currentX = e.touches[0].clientX;
      const diffX = touchStartX.current - currentX;
      const currentY = e.touches[0].clientY;
      const diffY = touchStartY.current - currentY;
      
      // 确保是水平滑动而不是垂直滚动
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 30) {
        // 防止页面滚动
        e.preventDefault();
        
        // 更新视觉反馈
        if (diffX > 30) {
          setSwipeDirection('left');
        } else if (diffX < -30) {
          setSwipeDirection('right');
        }
      }
    };
    
    const handleTouchEnd = (e) => {
      touchEndX.current = e.changedTouches[0].clientX;
      touchEndY.current = e.changedTouches[0].clientY;
      handleSwipe();
      
      // 清除滑动方向状态
      setTimeout(() => {
        setSwipeDirection(null);
      }, 300);
    };
    
    const handleSwipe = () => {
      const swipeDistanceX = touchStartX.current - touchEndX.current;
      const swipeDistanceY = touchStartY.current - touchEndY.current;
      const minSwipeDistance = 80; // 最小滑动距离
      
      // 确保是水平滑动而不是垂直滚动
      if (Math.abs(swipeDistanceX) > Math.abs(swipeDistanceY)) {
        if (swipeDistanceX > minSwipeDistance) {
          // 左滑，前进到下一题
          handleNextQuestion();
        } else if (swipeDistanceX < -minSwipeDistance) {
          // 右滑，返回上一题
          handlePrevQuestion();
        }
      }
    };
    
    const questionCard = questionCardRef.current;
    if (questionCard) {
      questionCard.addEventListener('touchstart', handleTouchStart, { passive: true });
      questionCard.addEventListener('touchmove', handleTouchMove, { passive: false });
      questionCard.addEventListener('touchend', handleTouchEnd, { passive: true });
    }
    
    return () => {
      if (questionCard) {
        questionCard.removeEventListener('touchstart', handleTouchStart);
        questionCard.removeEventListener('touchmove', handleTouchMove);
        questionCard.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [handleNextQuestion, handlePrevQuestion]);

  // 添加跳转到最近位置的功能
  const jumpToLastPosition = () => {
    const savedPosition = localStorage.getItem(`lastPosition-${bank.id}`);
    if (!savedPosition) {
      message.info('没有找到上次做题位置');
      return;
    }
    
    const position = JSON.parse(savedPosition);
    
    if (!position.isWrongQuestion) {
      // 确保索引在有效范围内
      const targetIndex = Math.min(position.index, (questionBank?.questions?.length || 1) - 1);
      setCurrentIndex(Math.max(0, targetIndex));
      setIsDoingWrongQuestion(false);
      setCurrentWrongQuestion(null);
      setWrongQuestionsQueue([]);
      resetAnswerState();
      message.success(`已跳转到上次做题位置: 第 ${targetIndex + 1} 题`);
    } else if (position.wrongQuestionId) {
      const targetQuestion = wrongAnswers.find(q => q.question === position.wrongQuestionId);
      if (targetQuestion) {
        setIsDoingWrongQuestion(true);
        setCurrentWrongQuestion(targetQuestion);
        setWrongQuestionsQueue([]);
        resetAnswerState();
        message.success('已跳转到上次错题练习位置');
      } else {
        message.warning('该错题已从错题集中移除，跳转到题库开始位置');
        setIsDoingWrongQuestion(false);
        setCurrentIndex(0);
        setCurrentWrongQuestion(null);
        setWrongQuestionsQueue([]);
        resetAnswerState();
      }
    }
  };

  // 题目导航功能
  const showQuestionNavigation = () => {
    setNavigationVisible(true);
  };
  
  const hideQuestionNavigation = () => {
    setNavigationVisible(false);
  };
  
  const jumpToQuestion = (index) => {
    if (index >= 0 && index < (questionBank?.questions?.length || 0)) {
      setCurrentIndex(index);
      setIsDoingWrongQuestion(false);
      setCurrentWrongQuestion(null);
      setWrongQuestionsQueue([]);
      resetAnswerState();
      hideQuestionNavigation();
      message.success(`已跳转到第 ${index + 1} 题`);
    }
  };
  
  // 渲染题目导航矩阵
  const renderQuestionNavigation = () => {
    if (!questionBank?.questions) return null;
    
    const questions = questionBank.questions;
    const gridItems = [];
    
    for (let i = 0; i < questions.length; i++) {
      const questionId = questions[i]?.question || '';
      const hasAnswered = !!questionRecords[questionId]?.userAnswer;
      const isCorrect = questionRecords[questionId]?.isCorrect;
      const isCurrent = i === currentIndex;
      
      let className = 'question-nav-item';
      if (isCurrent) {
        className += ' current';
      } else if (hasAnswered) {
        className += isCorrect ? ' correct' : ' wrong';
      }
      
      gridItems.push(
        <div
          key={i}
          className={className}
          onClick={() => jumpToQuestion(i)}
          title={`第${i + 1}题${hasAnswered ? (isCorrect ? ' (已答对)' : ' (答错)') : ' (未答)'}`}
        >
          {i + 1}
        </div>
      );
    }
    
    return (
      <div className="question-navigation-grid">
        {gridItems}
      </div>
    );
  };

  // 添加格式化正确答案的函数
  const formatCorrectAnswer = (answer, isMultiple, options = []) => {
    if (!answer) return '未知';
    
    if (isMultiple) {
      // 多选题：提取字母并格式化
      const letters = answer.match(/[A-E]/g) || [];
      if (letters.length > 0) {
        if (options.length > 0) {
          // 显示字母和对应的选项内容
          const optionTexts = letters.map(letter => {
            const option = options.find(opt => opt.startsWith(letter));
            return option ? `${letter}: ${option.substring(3)}` : letter;
          });
          return optionTexts.join('；');
        }
        return letters.join('、');
      }
      return answer;
    } else {
      // 单选题：保持原格式或提取字母
      const letter = answer.match(/^([A-E])/)?.[1];
      if (letter && options.length > 0) {
        const option = options.find(opt => opt.startsWith(letter));
        return option ? `${letter}: ${option.substring(3)}` : answer;
      }
      return answer;
    }
  };

  if (!questionBank) {
    return <div className="loading">加载题目中...</div>;
  }

  // 空题目对象，用作备用
  const emptyQuestion = {
    question: "加载中...",
    options: ["加载中..."],
    answer: "",
    explanation: "",
    detailedExplanation: "",
    relatedQuestions: []
  };

  // 确定当前显示的题目
  const currentQuestion = isDoingWrongQuestion 
    ? currentWrongQuestion 
    : (questionBank?.questions[currentIndex] || emptyQuestion);
    
  // 从答案中提取正确选项内容
  const correctAnswer = currentQuestion?.answer || '';
  const correctLetter = correctAnswer.match(/^([A-E])/)?.[1] || '';

  // 当前显示题目的答题状态
  const currentQuestionId = isDoingWrongQuestion 
    ? (currentWrongQuestion?.question || '') 
    : (questionBank?.questions[currentIndex]?.question || '');
  const hasAnswered = !!questionRecords[currentQuestionId]?.userAnswer;

  return (
    <div className="question-answering">
      <div className="header">
        <div className="nav-buttons">
          <Button onClick={onBack}>返回题库</Button>
          <Button 
            icon={<MenuOutlined />} 
            onClick={showDrawer}
            className="wrong-answers-button"
          >
            错题集 ({wrongAnswers.length})
          </Button>
          <Button
            icon={<AppstoreOutlined />}
            onClick={showQuestionNavigation}
            className="question-navigation-button"
          >
            题目导航
          </Button>
          {lastPosition && lastPosition.timestamp && (
            <Button
              onClick={jumpToLastPosition}
              className="last-position-button"
            >
              上次位置
            </Button>
          )}
        </div>
        
        {isDoingWrongQuestion ? (
          <div className="wrong-question-mode">
            {wrongQuestionsQueue.length > 0 ? (
              <Alert 
                message={`错题练习模式 (剩余${wrongQuestionsQueue.length}题)`} 
                type="warning" 
                showIcon 
              />
            ) : (
              <Alert message="错题练习模式" type="warning" showIcon />
            )}
          </div>
        ) : (
          <div className="stats">
            <span>得分: {score}</span>
            <span>进度: {currentIndex + 1}/{questionBank?.questions.length}</span>
            <span>正确率: {Math.round((progress.correct / (progress.answered || 1)) * 100)}%</span>
          </div>
        )}
      </div>

      <div 
        className={`question-card ${swipeDirection ? `swipe-${swipeDirection}` : ''} ${hasAnswered && !userAnswer ? 'answered' : ''}`} 
        ref={questionCardRef}
      >
        <div className="question-content">
          <div className="question-header">
            <div className="question-title">
              {isDoingWrongQuestion ? (
                <>
                  <span className="question-number">错题练习</span>
                  {currentWrongQuestion?.wrongCount && (
                    <span className="wrong-count-label">错误 {currentWrongQuestion.wrongCount} 次</span>
                  )}
                  {currentWrongQuestion?.bankTitle && (
                    <span className="chapter-label">来源: {currentWrongQuestion.bankTitle}</span>
                  )}
                </>
              ) : (
                <>
                  <span className="question-number">第 {currentIndex + 1} 题</span>
                  {bank?.title && (
                    <span className="chapter-label">章节: {bank.title}</span>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="question-text">
            {currentQuestion?.question}
          </div>
          {isMultipleChoice() && (
            <div className="question-type-indicator">
              <span className="multiple-choice-badge">多选题</span>
            </div>
          )}
        </div>
        
        <div className="options">
          {currentQuestion?.options?.map(option => {
            const optionLetter = option.match(/^([A-E])/)?.[1];
            
            // 修复多选题正确答案判断
            let isCorrect;
            if (isMultipleChoice()) {
              const correctAnswer = currentQuestion.answer || '';
              const correctLetters = correctAnswer.match(/[A-E]/g) || [];
              isCorrect = correctLetters.includes(optionLetter);
            } else {
              isCorrect = optionLetter === correctLetter;
            }
            
            // 对于多选题，判断选项是否被选中
            const isSelected = isMultipleChoice() && selectedOptions.includes(optionLetter);
            
            // 对于单选题，判断是否为用户选择的选项
            const isSingleSelected = !isMultipleChoice() && option === userAnswer;
            
            // 修复多选题的选项状态显示
            let optionClass = 'option';
            if (userAnswer) {
              if (isMultipleChoice()) {
                // 多选题：正确选项显示绿色，用户错选的显示红色
                if (isCorrect) {
                  optionClass += ' correct';
                } else if (isSelected) {
                  optionClass += ' wrong';
                }
              } else {
                // 单选题逻辑保持不变
                if (isCorrect) {
                  optionClass += ' correct';
                } else if (isSingleSelected) {
                  optionClass += ' wrong';
                }
              }
            } else {
              if (isSelected) {
                optionClass += ' selected';
              }
            }
            
            return (
              <div
                key={option}
                className={optionClass}
                onClick={() => !userAnswer && handleAnswerSelect(option)}
              >
                {isMultipleChoice() && !userAnswer && (
                  <Checkbox 
                    checked={isSelected}
                    className="option-checkbox"
                  />
                )}
                <span className="option-text">{option}</span>
                {userAnswer && isCorrect && (
                  <span className="correct-marker">✓</span>
                )}
                {userAnswer && isMultipleChoice() && isSelected && !isCorrect && (
                  <span className="wrong-marker">✗</span>
                )}
              </div>
            );
          })}
        </div>
        
        {!userAnswer && (
          <div className="answer-actions">
            {isMultipleChoice() ? (
              <Button 
                type="primary" 
                onClick={submitMultipleAnswer}
                className="submit-answer-button"
                disabled={selectedOptions.length === 0}
              >
                提交答案 ({selectedOptions.length}项)
              </Button>
            ) : null}
            <Button 
              onClick={showAnswer}
              className="show-answer-button"
            >
              查看答案和解析
            </Button>
          </div>
        )}
      </div>

      {userAnswer && (
        <div className="explanation-section">
          <Alert
            message={`正确答案: ${formatCorrectAnswer(correctAnswer, isMultipleChoice(), currentQuestion?.options)}`}
            type="success"
            showIcon
            className="correct-answer-alert"
          />
          
          <Tabs 
            defaultActiveKey="simple"
            items={[
              {
                key: 'simple',
                label: '解析',
                children: (
                  <div className="explanation-content">
                    {explanation ? (
                      <ReactMarkdown>{explanation}</ReactMarkdown>
                    ) : (
                      <p>暂无解析内容。可以根据题干和选项分析如下：</p>
                    )}
                    
                    <div className="auto-explanation">
                      <p>该题目的正确答案是：{formatCorrectAnswer(correctAnswer, isMultipleChoice(), currentQuestion?.options)}</p>
                      {!explanation && (
                        <p>解析：本题考察{currentQuestion.question.replace(/^\d+\.\s*\([^)]+\)/,'').trim()}相关知识点。
                        请根据教材内容理解该知识点的关键内容。</p>
                      )}
                    </div>
                  </div>
                )
              },
              ...(detailedExplanation ? [{
                key: 'detailed',
                label: '详细解析',
                children: (
                  <div className="explanation-content">
                    <ReactMarkdown>{detailedExplanation}</ReactMarkdown>
                  </div>
                )
              }] : []),
              ...(relatedQuestions.length > 0 ? [{
                key: 'related',
                label: '相关题目',
                children: (
                  <div className="related-questions">
                    {relatedQuestions.map((q, index) => (
                      <div key={index} className="related-question">
                        <p>{index + 1}. {q}</p>
                      </div>
                    ))}
                  </div>
                )
              }] : [])
            ]}
          />
          
          <div className="action-buttons">
            <Button onClick={handlePrevQuestion} icon={<LeftOutlined />}>上一题</Button>
            <Button 
              type="primary" 
              onClick={handleNextQuestion}
              className="next-button"
              icon={<RightOutlined />}
            >
              下一题
            </Button>
          </div>
        </div>
      )}

      {/* 滑动提示 */}
      {showSwipeHint && (
        <div className="swipe-hint visible">
          左滑进入下一题，右滑返回上一题
        </div>
      )}

      {/* 底部固定导航按钮 */}
      <div className="fixed-navigation">
        <Button 
          type="primary" 
          icon={<LeftOutlined />} 
          onClick={handlePrevQuestion}
          className="fixed-nav-button prev-button"
        >
          上一题
        </Button>
        <Button 
          type="primary" 
          icon={<RightOutlined />} 
          onClick={handleNextQuestion}
          className="fixed-nav-button next-button"
        >
          下一题
        </Button>
      </div>

      {/* 题目导航模态框 */}
      <Modal
        title={`题目导航 - ${bank.title}`}
        open={navigationVisible}
        onCancel={hideQuestionNavigation}
        footer={[
          <div key="legend" className="navigation-legend">
            <span className="legend-item">
              <span className="legend-color current"></span>
              当前题目
            </span>
            <span className="legend-item">
              <span className="legend-color correct"></span>
              已答对
            </span>
            <span className="legend-item">
              <span className="legend-color wrong"></span>
              答错
            </span>
            <span className="legend-item">
              <span className="legend-color unanswered"></span>
              未答
            </span>
          </div>,
          <Button key="close" onClick={hideQuestionNavigation}>
            关闭
          </Button>
        ]}
        width={800}
        className="question-navigation-modal"
      >
        {renderQuestionNavigation()}
      </Modal>

      <Drawer
        title={`错题集 (共 ${wrongAnswers.length} 题)`}
        placement="right"
        onClose={onCloseDrawer}
        open={drawerVisible}
        width={500}
        extra={
          <div className="drawer-actions">
            <Button 
              onClick={clearAllRecords}
              style={{ marginRight: 8 }}
            >
              清除答题记录
            </Button>
            <Button 
              onClick={clearMasteredQuestions}
              style={{ marginRight: 8 }}
              disabled={wrongAnswers.filter(q => q.status === 'mastered').length === 0}
            >
              清除已掌握
            </Button>
            <Button 
              type="primary" 
              danger 
              disabled={wrongAnswers.length === 0}
              onClick={clearWrongAnswers}
            >
              清空错题集
            </Button>
          </div>
        }
      >
        {wrongAnswers.length > 0 && (
          <div className="practice-all-button-container">
            <Button 
              type="primary"
              icon={<RetweetOutlined />}
              onClick={practiceAllWrongQuestions}
              className="practice-all-button"
              disabled={wrongAnswers.length === 0}
            >
              {wrongAnswers.filter(q => q.status !== 'mastered').length > 0 
                ? `重做未掌握错题 (${wrongAnswers.filter(q => q.status !== 'mastered').length}题)`
                : `重做所有错题 (${wrongAnswers.length}题)`
              }
            </Button>
          </div>
        )}
        
        <div className="wrong-answers-list">
          {wrongAnswers.length > 0 ? (
            <>
              {/* 未掌握的错题 */}
              {wrongAnswers.filter(q => q.status !== 'mastered').length > 0 && (
                <div className="wrong-answers-section">
                  <h3 className="section-title">未掌握错题 ({wrongAnswers.filter(q => q.status !== 'mastered').length}题)</h3>
                  {wrongAnswers
                    .filter(q => q.status !== 'mastered')
                    .sort((a, b) => (b.wrongCount || 1) - (a.wrongCount || 1)) // 按错误次数降序排列
                    .map((item, index) => {
                    // 判断是否为多选题
                    const isMultiple = getQuestionType(item) === 'multiple';
                    
                    // 用户答案处理
                    let userAnswerDisplay;
                    if (isMultiple) {
                      // 多选题：提取所有选择的字母
                      const userLetters = item.userAnswer?.match(/[A-E]/g) || [];
                      userAnswerDisplay = userLetters.length > 0 ? userLetters.join('、') : '未作答';
                    } else {
                      // 单选题：提取单个字母
                      const optionLetter = item.userAnswer?.match(/^([A-E])/)?.[1];
                      userAnswerDisplay = optionLetter || '未作答';
                    }
                    
                    // 正确答案处理
                    let correctAnswerDisplay;
                    if (isMultiple) {
                      // 多选题：直接从答案中提取所有正确字母
                      const correctLetters = item.answer?.match(/[A-E]/g) || [];
                      correctAnswerDisplay = correctLetters.length > 0 ? correctLetters.join('、') : '未知';
                    } else {
                      // 单选题：提取单个字母
                      const correctLetter = item.answer?.match(/^([A-E])/)?.[1];
                      correctAnswerDisplay = correctLetter || '未知';
                    }
                    
                    return (
                      <div key={`unmastered-${index}`} className="wrong-answer-item unmastered">
                        <div className="wrong-question-header">
                          <h4>{item.question}</h4>
                          <span className="wrong-count-badge">错误 {item.wrongCount || 1} 次</span>
                        </div>
                        <div className="wrong-question-options">
                          {item.options?.map(option => (
                            <div key={option} className="wrong-option">
                              {option}
                            </div>
                          ))}
                        </div>
                        <p>你的答案: <span className="wrong-answer">{userAnswerDisplay}</span></p>
                        <p>正确答案: <span className="correct-answer">{correctAnswerDisplay}</span></p>
                        <div className="wrong-explanation">
                          <ReactMarkdown>{cleanExplanation(item.explanation, item.question, item.answer)}</ReactMarkdown>
                        </div>
                        <Button 
                          type="primary"
                          onClick={() => practiceWrongQuestion(item)}
                          className="practice-button"
                        >
                          练习此题
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* 已掌握的错题 */}
              {wrongAnswers.filter(q => q.status === 'mastered').length > 0 && (
                <div className="wrong-answers-section">
                  <h3 className="section-title">已掌握错题 ({wrongAnswers.filter(q => q.status === 'mastered').length}题)</h3>
                  {wrongAnswers
                    .filter(q => q.status === 'mastered')
                    .sort((a, b) => (b.wrongCount || 1) - (a.wrongCount || 1)) // 按错误次数降序排列
                    .map((item, index) => {
                    // 判断是否为多选题
                    const isMultiple = getQuestionType(item) === 'multiple';
                    
                    // 用户答案处理
                    let userAnswerDisplay;
                    if (isMultiple) {
                      // 多选题：提取所有选择的字母
                      const userLetters = item.userAnswer?.match(/[A-E]/g) || [];
                      userAnswerDisplay = userLetters.length > 0 ? userLetters.join('、') : '未作答';
                    } else {
                      // 单选题：提取单个字母
                      const optionLetter = item.userAnswer?.match(/^([A-E])/)?.[1];
                      userAnswerDisplay = optionLetter || '未作答';
                    }
                    
                    // 正确答案处理
                    let correctAnswerDisplay;
                    if (isMultiple) {
                      // 多选题：从答案中提取所有正确字母
                      const answerLetters = item.answer?.split(':')[0] || item.answer || '';
                      const correctLetters = answerLetters.match(/[A-E]/g) || [];
                      correctAnswerDisplay = correctLetters.length > 0 ? correctLetters.join('、') : '未知';
                    } else {
                      // 单选题：提取单个字母
                      const correctLetter = item.answer?.match(/^([A-E])/)?.[1];
                      correctAnswerDisplay = correctLetter || '未知';
                    }
                    
                    return (
                      <div key={`mastered-${index}`} className="wrong-answer-item mastered">
                        <div className="wrong-question-header">
                          <h4>{item.question}</h4>
                          <span className="wrong-count-badge mastered">错误 {item.wrongCount || 1} 次</span>
                        </div>
                        <div className="wrong-question-options">
                          {item.options?.map(option => (
                            <div key={option} className="wrong-option">
                              {option}
                            </div>
                          ))}
                        </div>
                        <p>你的答案: <span className="wrong-answer">{userAnswerDisplay}</span></p>
                        <p>正确答案: <span className="correct-answer">{correctAnswerDisplay}</span></p>
                        <p className="mastered-info">✅ 已于 {new Date(item.masteredAt).toLocaleDateString()} 掌握</p>
                        <div className="wrong-explanation">
                          <ReactMarkdown>{cleanExplanation(item.explanation, item.question, item.answer)}</ReactMarkdown>
                        </div>
                        <Button 
                          onClick={() => practiceWrongQuestion(item)}
                          className="practice-button secondary"
                        >
                          复习此题
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <div className="empty-wrong-answers">
              <p>没有错题记录，太棒了！</p>
            </div>
          )}
        </div>
      </Drawer>
    </div>
  );
}

export default QuestionAnswering;